import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  getBilet,
  getCurrentPracticeSession,
  getLessonDrill,
  getQuestions,
  isGuest,
  type Lang,
  type PracticeDrill,
  type Question,
} from '@/api';
import { ErrorNotice } from '@/components/ErrorNotice';
import { GoRow } from '@/components/GoRow';
import { Loader } from '@/components/Loader';
import { Notice } from '@/components/Notice';
import { NOTICE_W } from '@/components/layout';
import { dataOf, useAsync, useRefreshable } from '@/hooks';
import { useLang } from '@/lang';
import { BiletSession } from '@/pages/practice/BiletSession';
import { SyncedSession } from '@/pages/practice/SyncedSession';
import { useDrillCursor } from '@/pages/practice/useDrillCursor';
import { RANDOM_SIZES } from '@/randomSet';
import { biletLabel, plural, topicName, useT } from '@/str';

const SESSION_SIZE = 20;
const WHOLE_SET = 500;

const LABEL_MAX = 64;

const clipLabel = (label: string) => [...label].slice(0, LABEL_MAX).join('');

function plainRound(lang: Lang, round: number) {
  const page = (skip: number) => getQuestions({ lang, limit: SESSION_SIZE, skip });
  return page(round * SESSION_SIZE)
    .then((l) => (l.data.length === 0 && l.count > 0 ? page(0) : l))
    .then((l) => ({ questions: l.data, hasMore: l.count > SESSION_SIZE }));
}

export function PracticePage({
  onlyWrong = false,
  filter,
  resume = false,
}: {
  onlyWrong?: boolean;
  filter?: 'tricky' | 'numeric';
  resume?: boolean;
}) {
  const navigate = useNavigate();
  const { lang } = useLang();
  const t = useT();
  const { topic, number, lesson, count } = useParams();
  const topicN = topic != null ? Number(topic) : undefined;
  const lessonN = lesson != null ? Number(lesson) : undefined;
  const biletN = number != null ? Number(number) : undefined;
  const randomN =
    count != null ? (RANDOM_SIZES.find((n) => n === Number(count)) ?? RANDOM_SIZES[0]) : undefined;

  const [sessionLang] = useState(lang);

  const [round, setRound] = useState(0);
  const [nonce, refresh] = useRefreshable();

  const unsolved = !onlyWrong && !filter && topicN == null && lessonN == null && randomN == null;

  const batch = topicN != null || filter ? WHOLE_SET : SESSION_SIZE;
  const skip = onlyWrong || unsolved ? 0 : round * batch;

  const state = useAsync<{
    questions: Question[];
    hasMore: boolean;
    resumed?: { id: string; from: number; lang: Lang };
  }>(
    () =>
      resume
        ? getCurrentPracticeSession().then((drill) => ({
            questions: drill?.questions ?? [],
            hasMore: false,
            resumed: drill ? { id: drill.id, from: drill.position, lang: drill.lang } : undefined,
          }))
        : biletN != null
          ? getBilet(biletN, sessionLang).then((b) => ({
              questions: b.questions,
              hasMore: false,
            }))
          : randomN != null
            ? getQuestions({ lang: sessionLang, limit: randomN, shuffle: true }).then((l) => ({
                questions: l.data,
                hasMore: false,
              }))
            : lessonN != null
              ? getLessonDrill(lessonN, sessionLang, skip, SESSION_SIZE).then((l) => ({
                  questions: l.data,
                  hasMore: skip + l.data.length < l.count,
                }))
              : unsolved && isGuest()
                ? plainRound(sessionLang, round)
                : getQuestions({
                    lang: sessionLang,
                    limit: batch,
                    review: onlyWrong,
                    unsolved,
                    skip,
                    topic: topicN,
                    tricky: filter === 'tricky',
                    numeric: filter === 'numeric',
                  }).then((l) =>
                    unsolved && l.count === 0
                      ? plainRound(sessionLang, round)
                      : { questions: l.data, hasMore: skip + l.data.length < l.count },
                  ),
    [onlyWrong, filter, sessionLang, topicN, lessonN, biletN, randomN, skip, round, nonce, resume],
  );

  const loaded = dataOf(state);

  const sessionLabel = onlyWrong
    ? t.drillShort
    : filter
      ? t[filter]
      : randomN != null
        ? t.randomHead.replace('{n}', String(randomN)).replace('{u}', plural(randomN, t.randomUnit))
        : topicN != null
          ? topicName(topicN, t)
          : t.practiceHead;

  const drawnIds = loaded?.questions.map((q) => q.id).join(',') ?? '';

  const drill = useMemo<PracticeDrill | null>(() => {
    if (resume || drawnIds === '') return null;
    if (biletN != null)
      return { lang: sessionLang, label: clipLabel(biletLabel(biletN, lang)), bilet: biletN };
    if (lessonN != null) return { lang: sessionLang, lesson: lessonN, skip, limit: SESSION_SIZE };
    return { lang: sessionLang, label: clipLabel(sessionLabel), question_ids: drawnIds.split(',') };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume, drawnIds, biletN, lessonN, sessionLang, lang, skip]);

  const cursor = useDrillCursor(drill, loaded?.resumed?.id ?? null, loaded?.resumed?.from ?? 0);

  if (state.loading) return <Loader />;

  if (state.error !== null) {
    return (
      <ErrorNotice error={state.error} onRetry={() => void refresh().catch(() => {})}>
        <GoRow className={NOTICE_W} onClick={() => navigate('/')}>
          {t.back}
        </GoRow>
      </ErrorNotice>
    );
  }

  if (state.data.questions.length === 0) {
    return (
      <Notice
        sign={onlyWrong ? '✓' : '?'}
        title={onlyWrong ? t.noWrongTitle : t.noQuestions}
        body={onlyWrong ? t.noWrongSub : filter === 'tricky' ? t.noTrickySub : undefined}
      >
        <GoRow className={NOTICE_W} onClick={() => navigate('/')}>
          {t.goHome}
        </GoRow>
      </Notice>
    );
  }

  if (biletN != null) {
    return (
      <BiletSession
        key={biletN}
        initial={state.data.questions}
        biletN={biletN}
        initialLang={sessionLang}
        t={t}
        onProgress={cursor.advance}
        onFinish={cursor.finish}
      />
    );
  }
  return (
    <SyncedSession
      key={round}
      initial={state.data.questions}
      initialLang={state.data.resumed?.lang ?? sessionLang}
      t={t}
      onDone={() => navigate('/')}
      onMore={state.data.hasMore ? () => setRound((r) => r + 1) : undefined}
      onProgress={cursor.advance}
      onFinish={cursor.finish}
      label={sessionLabel}
    />
  );
}
